import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ClientSubscriptionService,
  TimeconversionService,
  ToastMessageService,
  AdvancedSettingService,
  AuthSevices
} from '@fitness-force/api';
import {
  ChangeStartDateRequest,
  ChangeEndDateRequest,
  RecurringTemplateNonRecurringService,
  SubscriptionById,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';

import { Observable } from 'rxjs';

@Component({
  selector: 'subscription-start-end-date-edit',
  templateUrl: './subscription-start-end-date-edit.component.html',
  providers: [],
  standalone: true,
  imports: [    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    DatePickerModule,
    MessageModule,
    TranslateModule],
})
export class SubscriptionStartEndDateEditComponent implements OnInit {
  @Input() visible = false;  
  @Input() subscription: SubscriptionById;
  @Input() clientId: number = 0;
  @Input() tenantId: number = 0;
  @Input() membershipId: number = 0;
  @Output() closeEvent: EventEmitter<any> = new EventEmitter();
  @Output() reloadSubscription: EventEmitter<any> = new EventEmitter();

  EditSubscriptionDatesForm: UntypedFormGroup = new UntypedFormGroup({});
  isFormSubmitting: boolean = false;

  dateLocked: boolean = true;
  membership: RecurringTemplateNonRecurringService;
  startDateChanged: boolean = true;
  maxServiceEndDate: any;
  dateError: boolean = false;

  memberships: RecurringTemplateNonRecurringService[] = [];

  subscriptionStartDate: Date;
  subscriptionEndDate: Date;
  maxSubscriptionStartDate: Date;
  private currentLoggedinUser: any;

  constructor(
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private toastMessageService: ToastMessageService,
    public translate: TranslateService,
    private clientSubscriptionService: ClientSubscriptionService,
    private timeService: TimeconversionService,
    private advanceSettingService: AdvancedSettingService,
    private authService: AuthSevices
  ) {}

  ngOnInit(): void {
    this.currentLoggedinUser = this.authService.currentUserDetails();
    this.getTenantSettings();
    this.componentInit();
  }

  getTenantSettings() {
    this.advanceSettingService.getAdvanceSetting(this.tenantId,'limit_subscription_future_start_in_days').subscribe((res: any) => {
      this.maxSubscriptionStartDate = new Date(new Date().setDate(new Date().getDate() + parseInt(res.value)));
    });
  }

  componentInit() {
    this.subscriptionStartDate = this.timeService.ConvertUTCToLocalDateTime(
      this.subscription.start_date
    );
    this.subscriptionEndDate = this.timeService.ConvertUTCToLocalDateTime(
      this.subscription.end_date
    );
    this.memberships =this.subscription.recurring_template_non_recurring_services;
    if (this.membershipId > 0) {
      this.membership = this.memberships.find(
        (o) => o.client_membership_id == this.membershipId
      )!;
      this.EditSubscriptionDatesForm = this.fb.group({
        start_date: [
          this.membership.service_start_date,
          Validators.required
  ],
        end_date: [
          { value: this.membership.service_end_date, disabled: true },
          Validators.required
  ],
        comment: [''],
      });
    } else {
      this.EditSubscriptionDatesForm = this.fb.group({
        start_date: [
          this.timeService.ConvertUTCToLocalDateTime(
            this.subscription.start_date
          ),
          Validators.required
  ],
        end_date: [
          {
            value: this.timeService.ConvertUTCToLocalDateTime(
              this.subscription.end_date
            ),
            disabled: true,
          },
          Validators.required
  ],
        comment: [''],
      });
    }
    let maxServiceDate = this.memberships.reduce((first, second) =>
      first.service_end_date > second.service_end_date ? first : second
    );
    this.maxServiceEndDate = new Date(
      maxServiceDate!.service_end_date
    ).setHours(0, 0, 0, 0);
  }

  toggleDateLock(dateLocked: boolean) {
    this.dateLocked = !dateLocked;
    if (this.membershipId > 0) {
      this.StartDateFormControl.setValue(
        new Date(this.membership.service_start_date)
      );
      this.EndDateFormControl.setValue(
        new Date(this.membership.service_end_date)
      );
    } else {
      this.StartDateFormControl.setValue(
        new Date(this.subscription.start_date)
      );
      this.EndDateFormControl.setValue(new Date(this.subscription.end_date));
    }
    if (this.dateLocked) {
      this.EndDateFormControl.disable();
      this.StartDateFormControl.enable();
    } else {
      this.EndDateFormControl.enable();
      this.StartDateFormControl.disable();
    }

    this.startDateChanged = true;
  }

  OnStartDateChange() {
    const selectedDate = new Date(this.StartDateFormControl.value);
    let previousStartDate = new Date();
    if (this.membershipId > 0)
    previousStartDate = new Date(this.membership.service_start_date);
    else previousStartDate = new Date(this.subscription.start_date);

    const dateDiff = this.calculateDiff(previousStartDate, selectedDate);
    const existingEndDate = new Date(this.EndDateFormControl.value);
    const newEndDate = new Date(existingEndDate.setDate(existingEndDate.getDate() + dateDiff)).setHours(0, 0, 0, 0);

    if (this.membershipId > 0) {
      if (newEndDate > new Date(this.subscriptionEndDate).setHours(0, 0, 0, 0)) {
        this.translate.get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      } else if (selectedDate.setHours(0, 0, 0, 0) < new Date(this.subscriptionStartDate).setHours(0, 0, 0, 0)) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB_START_DATE').subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      }
      else{
        let updatedNewDate = new Date(new Date(newEndDate).setHours(new Date().getHours(),new Date().getMinutes(),new Date().getSeconds(),new Date().getMilliseconds()));
        this.EndDateFormControl.setValue(updatedNewDate);
      }
     
    } else {
      if (new Date(newEndDate) < new Date(this.maxServiceEndDate)) {
        this.translate
        .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_SUBS').subscribe((translation) => {
          this.toastMessageService.showErrorMessage(translation);
        });
      }
      else{
        let updatedNewDate = new Date(new Date(newEndDate).setHours(new Date().getHours(),new Date().getMinutes(),new Date().getSeconds(),new Date().getMilliseconds()));
        this.EndDateFormControl.setValue(updatedNewDate);
      }
    }
    this.startDateChanged = true;
  }

  OnEndDateChange(event: any) {
    let EndDate = new Date(this.EndDateFormControl.value);
    let newEndDate= new Date(EndDate).setHours(0,0,0,0);
    if (this.membershipId > 0) {
      if (newEndDate > new Date(this.subscriptionEndDate).setHours(0,0,0,0)) {
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_MEMB')
          .subscribe((translation) => {
            this.toastMessageService.showErrorMessage(translation);
          });
      }
    } else {
      // if (newEndDate < this.maxServiceEndDate) {
      //   this.translate
      //     .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.FOR_SUBS')
      //     .subscribe((translation) => {
      //       this.toastMessageService.showErrorMessage(translation);
      //     });
      // }
    }
    this.startDateChanged = false;
  }

  calculateDiff(previousDate: Date, currentDate: Date) {
    return Math.floor(
      (Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      ) -
        Date.UTC(
          previousDate.getFullYear(),
          previousDate.getMonth(),
          previousDate.getDate()
        )) /
        (1000 * 60 * 60 * 24)
    );
  }

  UpdateStartEndDate() {
    if (this.EditSubscriptionDatesForm.valid) {
      if (this.membershipId > 0) {
        this.changeMembershipDates();
      } else {
        this.changeSubscriptionDates();
      }
    } else {
      Object.keys(this.EditSubscriptionDatesForm.controls).forEach((field) => {
        const control = this.EditSubscriptionDatesForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  changeMembershipDates() {
    this.isFormSubmitting = true;
    let service = new Observable<any>();
    if (this.startDateChanged) {
      this.StartDateFormControl.value.setHours(0,0,0);
      let editDateRequest: ChangeStartDateRequest = {} as ChangeStartDateRequest;
      editDateRequest.start_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.StartDateFormControl.value
      );
      editDateRequest.comment = this.CommentFormControl.value;
      service = this.clientSubscriptionService.ChangeMembershipStartDate(
        this.subscription.tenant_id,
        this.clientId,
        this.subscription.id,
        this.membershipId,
        editDateRequest
      );
    } else {
      this.EndDateFormControl.value.setHours(23,59,59);
      let editDateRequest: ChangeEndDateRequest = {} as ChangeEndDateRequest;
      editDateRequest.end_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.EndDateFormControl.value
      );
      editDateRequest.comment = this.CommentFormControl.value;
      service = this.clientSubscriptionService.ChangeMembershipEndDate(
        this.subscription.tenant_id,
        this.clientId,
        this.subscription.id,
        this.membershipId,
        editDateRequest
      );
    }
    service.subscribe({
      next: async (res: any) => {
        this.isFormSubmitting = false;
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.SUCCESS_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        await this.timeout(2000);
        this.subscription.recurring_template_non_recurring_services.forEach(
          (o) => {
            if (o.client_membership_id == this.membershipId) {
                o.service_start_date = this.StartDateFormControl.value;
                o.service_end_date = this.EndDateFormControl.value;
            }
          }
        );
        this.closePopup(true);
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  changeSubscriptionDates() {
    this.isFormSubmitting = true;
    let service = new Observable<any>();
    if (this.startDateChanged) {
      this.StartDateFormControl.value.setHours(0,0,0);
      let editDateRequest: ChangeStartDateRequest = {} as ChangeStartDateRequest;
      editDateRequest.start_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.StartDateFormControl.value
      );
      editDateRequest.comment = this.CommentFormControl.value;
      service = this.clientSubscriptionService.ChangeSubscriptionStartDate(
        this.subscription.tenant_id,
        this.clientId,
        this.subscription.id,
        editDateRequest
      );
    } else {
      this.EndDateFormControl.value.setHours(23,59,59);
      let editDateRequest: ChangeEndDateRequest = {} as ChangeEndDateRequest;
      editDateRequest.end_date = this.timeService.ConvertLocalDateTimeToUTC(
        this.EndDateFormControl.value
      );
      editDateRequest.comment = this.CommentFormControl.value;
      service = this.clientSubscriptionService.ChangeSubscriptionEndDate(
        this.subscription.tenant_id,
        this.clientId,
        this.subscription.id,
        editDateRequest
      );
    }
    service.subscribe({
      next: async (res: any) => {
        this.isFormSubmitting = false;
        this.translate
          .get('SUBSCRIPTIONS.DATE_CHANGES.MSG.SUCCESS_MSG')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
          await this.timeout(2000);
          this.subscription.start_date = this.StartDateFormControl.value.toUTCString();
          this.subscription.end_date =this.EndDateFormControl.value.toUTCString();
          this.reloadData(true);
          this.closePopup(true);
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        this.toastMessageService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  closePopup(value: boolean) {
    this.visible = false;
    this.closeEvent.emit(this.subscription);
  }

  reloadData(value:boolean)
  {
    this.reloadSubscription.emit(value);
  }



  get StartDateFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .start_date as UntypedFormControl;
  }

  get EndDateFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .end_date as UntypedFormControl;
  }

  get CommentFormControl(): UntypedFormControl {
    return this.EditSubscriptionDatesForm.controls
      .comment as UntypedFormControl;
  }

  canBypassMaxSubscriptionStartDate(): boolean {
    return this.currentLoggedinUser && (this.currentLoggedinUser.role.toLowerCase() == 'admin' || this.currentLoggedinUser.can_admin_billing == 'true');
  }

  timeout(ms: any) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

}
